import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Redo } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import Spinner from "@/components/ui/spinner";
import Text from "@/components/ui/text";
import DreamerAttachmentModalShell from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentModalShell";
import { useReportModalHeader } from "@/components/global/Chat/components/dreamerReports/useReportModalHeader";
import { useReportRuns } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";
import { ReportPreviewTarget } from "@/entities/savedReports/types";

const RUN_STATE_TRANSLATION_KEY = `${TRANSLATION_KEY}.runState`;

// The report is already saved, so the view has no Save control to offer.
const renderNoSaveControl = () => null;

interface ReportRunStateModalProps {
  report: ReportPreviewTarget;
  isOpen: boolean;
  isFailed: boolean;
  onClose: () => void;
}

/**
 * A report's expanded view while it has no result to show: generating, or
 * failed to generate. The header is the report's usual one, with nothing to
 * download yet. Once the run succeeds the host swaps the result in its place.
 */
export const ReportRunStateModal: React.FC<ReportRunStateModalProps> = ({
  report,
  isOpen,
  isFailed,
  onClose
}) => {
  const { t } = useTranslation();
  const { startRun } = useReportRuns();
  const reportName = report.name || t(`${TRANSLATION_KEY}.untitled`);

  const { title, headerSlots } = useReportModalHeader({
    savedReportId: report.id,
    savedName: report.name,
    messageId: report.originMessageId,
    // Only a report that can run again has a run to show the state of.
    isReplayable: report.isReplayable ?? true,
    regeneratesInPlace: true,
    renderSaveControl: renderNoSaveControl,
    onClose
  });

  const setIsOpen = useCallback(
    (open: React.SetStateAction<boolean>) => {
      const nextValue = typeof open === "function" ? open(isOpen) : open;
      if (!nextValue) onClose();
    },
    [isOpen, onClose]
  );

  return (
    <DreamerAttachmentModalShell
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={title ?? reportName}
      {...headerSlots}
      footer={
        isFailed ? undefined : (
          <Text variant="bodySecondary">
            {t(`${RUN_STATE_TRANSLATION_KEY}.generating`)}
          </Text>
        )
      }
    >
      {isFailed ? (
        <div
          className="flex flex-1 flex-col items-center justify-center gap-4 text-center"
          data-test="report-run-failed"
        >
          <Text variant="body">{t(`${RUN_STATE_TRANSLATION_KEY}.failed`)}</Text>
          <Button
            variant="secondary"
            onClick={() => startRun(report.id, report.name || null)}
            data-test="report-run-try-again"
          >
            <IconContainer size="sm">
              <Redo />
            </IconContainer>
            {t(`${RUN_STATE_TRANSLATION_KEY}.tryAgain`)}
          </Button>
        </div>
      ) : (
        <div
          className="flex flex-1 flex-col items-center justify-center gap-3 text-center"
          data-test="report-run-generating"
        >
          <Spinner size="lg" />
          <div className="flex max-w-[320px] flex-col gap-1">
            <Text variant="body">
              {t(`${RUN_STATE_TRANSLATION_KEY}.generating`)}
            </Text>
            <Text variant="bodySecondary">
              {t(`${RUN_STATE_TRANSLATION_KEY}.generatingDescription`)}
            </Text>
          </div>
        </div>
      )}
    </DreamerAttachmentModalShell>
  );
};
